import { ChangeEvent, RefObject, useEffect, useId, useMemo, useRef, useState } from "react";

export type DateInputParts = {
  month: string;
  day: string;
  year: string;
};

const EMPTY_DATE_PARTS: DateInputParts = { month: "", day: "", year: "" };

function pad2(value: number): string {
  return String(value).padStart(2, "0");
}

export function sanitizeDateInputPart(raw: string, maxLength: number): string {
  return raw.replace(/[^\d]/g, "").slice(0, maxLength);
}

export function parseDateInputValue(value: string | null | undefined): DateInputParts {
  if (!value) return { ...EMPTY_DATE_PARTS };
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return { ...EMPTY_DATE_PARTS };
  return {
    month: match[2],
    day: match[3],
    year: match[1]
  };
}

export function areDateInputPartsBlank(parts: DateInputParts): boolean {
  return !parts.month && !parts.day && !parts.year;
}

export function dateInputPartsToValue(parts: DateInputParts): string | null {
  if (!parts.month || !parts.day || parts.year.length !== 4) return null;

  const month = Number(parts.month);
  const day = Number(parts.day);
  const year = Number(parts.year);
  if (!Number.isInteger(month) || !Number.isInteger(day) || !Number.isInteger(year)) return null;
  if (month < 1 || month > 12 || day < 1 || day > 31 || year < 1000) return null;

  const parsed = new Date(Date.UTC(year, month - 1, day));
  if (
    parsed.getUTCFullYear() !== year ||
    parsed.getUTCMonth() !== month - 1 ||
    parsed.getUTCDate() !== day
  ) {
    return null;
  }

  return `${year}-${pad2(month)}-${pad2(day)}`;
}

export function dateInputValidationMessage(
  parts: DateInputParts,
  options: { allowEmpty?: boolean; required?: boolean } = {}
): string {
  const allowEmpty = options.allowEmpty ?? !options.required;
  if (areDateInputPartsBlank(parts)) return allowEmpty ? "" : "Enter a date.";
  return dateInputPartsToValue(parts) ? "" : "Enter a valid date.";
}

export function DateInput({
  label,
  value,
  onChange,
  required,
  disabled,
  allowEmpty,
  hideLabel
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  disabled?: boolean;
  allowEmpty?: boolean;
  hideLabel?: boolean;
}) {
  const id = useId();
  const blankAllowed = allowEmpty ?? !required;
  const [parts, setParts] = useState<DateInputParts>(() => parseDateInputValue(value));
  const monthRef = useRef<HTMLInputElement | null>(null);
  const dayRef = useRef<HTMLInputElement | null>(null);
  const yearRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    setParts(parseDateInputValue(value));
  }, [value]);

  const validationMessage = useMemo(
    () => dateInputValidationMessage(parts, { allowEmpty: blankAllowed, required }),
    [blankAllowed, parts, required]
  );

  useEffect(() => {
    yearRef.current?.setCustomValidity(validationMessage);
  }, [validationMessage]);

  useEffect(() => {
    const nextValue = dateInputPartsToValue(parts);
    if (nextValue) {
      if (nextValue !== value) onChange(nextValue);
      return;
    }
    if (blankAllowed && areDateInputPartsBlank(parts) && value) onChange("");
  }, [blankAllowed, onChange, parts, value]);

  const updatePart = (part: keyof DateInputParts, maxLength: number, raw: string, nextRef?: RefObject<HTMLInputElement>) => {
    const nextPart = sanitizeDateInputPart(raw, maxLength);
    setParts((prev) => ({ ...prev, [part]: nextPart }));
    if (nextPart.length >= maxLength) {
      window.setTimeout(() => nextRef?.current?.focus(), 0);
    }
  };

  return (
    <fieldset className="date-input-field stack-xs" disabled={disabled}>
      <legend className={`label${hideLabel ? " sr-only" : ""}`}>{label}</legend>
      <div className="segmented-date-input" role="group" aria-label={label}>
        <input
          ref={monthRef}
          id={`${id}-month`}
          className="input date-part-input"
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          placeholder="MM"
          value={parts.month}
          maxLength={2}
          required={required || !blankAllowed}
          aria-label={`${label} month`}
          aria-invalid={Boolean(validationMessage)}
          onFocus={(event) => event.currentTarget.select()}
          onChange={(event: ChangeEvent<HTMLInputElement>) => updatePart("month", 2, event.target.value, dayRef)}
        />
        <span className="date-separator" aria-hidden>
          /
        </span>
        <input
          ref={dayRef}
          id={`${id}-day`}
          className="input date-part-input"
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          placeholder="DD"
          value={parts.day}
          maxLength={2}
          required={required || !blankAllowed}
          aria-label={`${label} day`}
          aria-invalid={Boolean(validationMessage)}
          onFocus={(event) => event.currentTarget.select()}
          onChange={(event: ChangeEvent<HTMLInputElement>) => updatePart("day", 2, event.target.value, yearRef)}
        />
        <span className="date-separator" aria-hidden>
          /
        </span>
        <input
          ref={yearRef}
          id={`${id}-year`}
          className="input date-part-input date-year-input"
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          placeholder="YYYY"
          value={parts.year}
          maxLength={4}
          required={required || !blankAllowed}
          aria-label={`${label} year`}
          aria-invalid={Boolean(validationMessage)}
          onFocus={(event) => event.currentTarget.select()}
          onChange={(event: ChangeEvent<HTMLInputElement>) => updatePart("year", 4, event.target.value)}
        />
      </div>
    </fieldset>
  );
}
